/**
 * finance-core — Ngân sách phong bì (GĐ3). Mỗi danh mục một hạn mức mỗi tháng;
 * thấy ngay đã chi, đã cam kết (dự chi chờ xác nhận), còn lại. Thuần hàm, có test.
 *
 * Danh mục luôn theo TÊN (dữ liệu thật lưu tên danh mục, không phải id).
 * Chi tiêu dùng chiều có dấu (hoàn tiền trừ đi). Tiền vay giải ngân không phải thu nhập.
 */
import { signedAmount, isIncome, type TxRecord } from './aggregate';

export type BudgetLevel = 'none' | 'ok' | 'warn' | 'bad';

export interface BudgetLine {
  category: string;
  limit: number;
  /** Phần dư chuyển từ tháng trước (khi bật rollover) */
  carry: number;
  /** limit + carry */
  available: number;
  spent: number;
  committed: number;
  /** available − spent − committed (có thể âm) */
  remaining: number;
  /** (spent + committed) / available × 100; null khi available = 0 */
  pct: number | null;
  level: BudgetLevel;
}

export interface BudgetStatus {
  lines: BudgetLine[];
  /** Chỉ tính các danh mục ĐÃ đặt hạn mức — so chi toàn bộ với hạn mức một phần sẽ gây hiểu nhầm */
  totals: { available: number; spent: number; committed: number; remaining: number; pct: number | null; level: BudgetLevel };
  /** Danh mục có chi nhưng chưa đặt hạn mức */
  unbudgeted: Array<{ category: string; spent: number }>;
}

export interface EnvExpense extends TxRecord { category?: string; categoryName?: string; autoGenerated?: boolean }
export interface PlannedLike { status?: string; amount?: number; category?: string; categoryName?: string; year?: number; month?: number }

const catOf = (r: { category?: string; categoryName?: string }) => r.category || r.categoryName || 'Khác';
export const levelOf = (pct: number | null): BudgetLevel => (pct === null ? 'none' : pct >= 100 ? 'bad' : pct >= 80 ? 'warn' : 'ok');

export const budgetStatus = (input: {
  limits: Record<string, number>;
  carry?: Record<string, number>;
  year: number;
  month: number;
  expenses: EnvExpense[];
  planned?: PlannedLike[];
}): BudgetStatus => {
  const { limits, carry = {}, year, month } = input;
  const spentBy = new Map<string, number>();
  for (const e of input.expenses) {
    if (e.year !== year || e.month !== month) continue;
    spentBy.set(catOf(e), (spentBy.get(catOf(e)) ?? 0) + signedAmount(e));
  }
  const commitBy = new Map<string, number>();
  for (const p of input.planned ?? []) {
    if (p.status !== 'pending' || p.year !== year || p.month !== month) continue;
    commitBy.set(catOf(p), (commitBy.get(catOf(p)) ?? 0) + (Number(p.amount) || 0));
  }

  const lines: BudgetLine[] = Object.entries(limits).map(([category, rawLimit]) => {
    const limit = Number(rawLimit) || 0;
    const c = Number(carry[category]) || 0;
    const available = limit + c;
    const spent = spentBy.get(category) ?? 0;
    const committed = commitBy.get(category) ?? 0;
    const pct = available > 0 ? ((spent + committed) / available) * 100 : null;
    return { category, limit, carry: c, available, spent, committed, remaining: available - spent - committed, pct, level: available > 0 ? levelOf(pct) : spent + committed > 0 ? 'bad' : 'none' };
  }).sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1));

  const sum = (f: (l: BudgetLine) => number) => lines.reduce((s, l) => s + f(l), 0);
  const available = sum(l => l.available), spent = sum(l => l.spent), committed = sum(l => l.committed);
  const pct = available > 0 ? ((spent + committed) / available) * 100 : null;

  const unbudgeted = [...spentBy.entries()]
    .filter(([cat, v]) => !(cat in limits) && Math.abs(v) > 0)
    .map(([category, v]) => ({ category, spent: v }))
    .sort((a, b) => b.spent - a.spent);

  return { lines, totals: { available, spent, committed, remaining: available - spent - committed, pct, level: levelOf(pct) }, unbudgeted };
};

/** Phần dư tháng trước chuyển sang (chỉ khi còn dương, một cấp — không cộng dồn nhiều tháng). */
export const rolloverCarry = (prev: BudgetStatus): Record<string, number> =>
  Object.fromEntries(prev.lines.map(l => [l.category, Math.max(0, l.available - l.spent)]).filter(([, v]) => (v as number) > 0));

/** Gợi ý hạn mức: chi trung bình/tháng của N tháng ĐÃ KẾT THÚC gần nhất (bỏ khoản tự sinh), làm tròn LÊN theo `step`. */
export const suggestLimits = (expenses: EnvExpense[], now: Date = new Date(), months = 3, step = 50_000): Record<string, number> => {
  const keys = new Set<string>();
  for (let i = 1; i <= months; i++) { const d = new Date(now.getFullYear(), now.getMonth() - i, 1); keys.add(`${d.getFullYear()}-${d.getMonth() + 1}`); }
  const total = new Map<string, number>();
  const active = new Set<string>();
  for (const e of expenses) {
    const k = `${e.year}-${e.month}`;
    if (!keys.has(k) || e.autoGenerated) continue;
    active.add(k);
    total.set(catOf(e), (total.get(catOf(e)) ?? 0) + signedAmount(e));
  }
  const n = Math.max(1, active.size);
  const out: Record<string, number> = {};
  for (const [cat, sum] of total) {
    const avg = sum / n;
    if (avg > 0) out[cat] = Math.ceil(avg / step) * step;
  }
  return out;
};

export interface AnnualItem { category: string; yearTotal: number; perMonth: number; months: number }
/**
 * Gợi ý ngân sách tách khoản ĐÓNG THEO NĂM (vd. bảo hiểm): danh mục chỉ phát sinh ở ≤ 2 trong 12 tháng gần nhất
 * không đặt hạn mức tháng (sẽ báo vượt giả) mà tính số cần để dành mỗi tháng. Cần ≥ 6 tháng có dữ liệu mới nhận diện.
 */
export const suggestBudget = (expenses: EnvExpense[], now: Date = new Date(), months = 3, step = 50_000): { limits: Record<string, number>; annual: AnnualItem[] } => {
  const limits = suggestLimits(expenses, now, months, step);
  const keys = new Set<string>();
  for (let i = 1; i <= 12; i++) { const d = new Date(now.getFullYear(), now.getMonth() - i, 1); keys.add(`${d.getFullYear()}-${d.getMonth() + 1}`); }
  const perCat = new Map<string, Map<string, number>>();
  const active = new Set<string>();
  for (const e of expenses) {
    const k = `${e.year}-${e.month}`;
    if (!keys.has(k) || e.autoGenerated) continue;
    active.add(k);
    const m = perCat.get(catOf(e)) ?? new Map<string, number>();
    m.set(k, (m.get(k) ?? 0) + signedAmount(e));
    perCat.set(catOf(e), m);
  }
  const annual: AnnualItem[] = [];
  if (active.size >= 6) {
    for (const [category, m] of perCat) {
      const yearTotal = [...m.values()].reduce((a, b) => a + b, 0);
      if (m.size <= 2 && yearTotal > 0) { annual.push({ category, yearTotal, perMonth: Math.ceil(yearTotal / 12 / step) * step, months: m.size }); delete limits[category]; }
    }
  }
  return { limits, annual: annual.sort((a, b) => b.yearTotal - a.yearTotal) };
};

/** Ngân sách kiểu cũ `{categories: {id: {name, amount}}}` → hạn mức theo TÊN danh mục. */
export const legacyBudgetLimits = (b: { categories?: Record<string, { name?: string; amount?: number }> } | undefined | null): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [id, c] of Object.entries(b?.categories ?? {})) {
    const name = (c?.name || id).trim();
    out[name] = (out[name] ?? 0) + (Number(c?.amount) || 0);
  }
  return out;
};

export interface EnvelopeAlert { id: string; level: 'warn' | 'bad'; title: string; detail: string }
const vnd = (n: number) => `${Math.round(n).toLocaleString('vi-VN')} đ`;

/** Cảnh báo ≥80% (chú ý) và ≥100% (vượt) theo từng danh mục. */
export const envelopeAlerts = (status: BudgetStatus, month: number): EnvelopeAlert[] =>
  status.lines.filter(l => l.level === 'warn' || l.level === 'bad').map(l => ({
    id: `budget:${l.category}`, level: l.level as 'warn' | 'bad',
    title: l.level === 'bad' ? `Ngân sách ${l.category} tháng ${month} ${l.remaining < 0 ? 'đã vượt' : 'đã hết'}` : `Ngân sách ${l.category} tháng ${month} sắp hết`,
    detail: l.level === 'bad'
      ? `Đã dùng ${vnd(l.spent + l.committed)} / ${vnd(l.available)} — ${l.remaining < 0 ? `vượt ${vnd(-l.remaining)}` : 'đã dùng hết ngân sách'}.`
      : `Đã dùng ${Math.round(l.pct ?? 0)}% — còn ${vnd(l.remaining)}.`
  }));

/** Quy tắc 50/30/20 đo thật: thiết yếu / mong muốn theo nhãn `budgetClass` của danh mục (chưa gán → mong muốn). */
export const rule503020 = (input: {
  incomes: TxRecord[]; expenses: EnvExpense[]; year: number; month: number;
  classByName: Record<string, 'need' | 'want' | undefined>;
}) => {
  const income = input.incomes.filter(r => isIncome(r) && r.year === input.year && r.month === input.month).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  let needs = 0, wants = 0;
  for (const e of input.expenses) {
    if (e.year !== input.year || e.month !== input.month) continue;
    if (input.classByName[catOf(e)] === 'need') needs += signedAmount(e); else wants += signedAmount(e);
  }
  const savings = income - needs - wants;
  const pct = (v: number) => (income > 0 ? (v / income) * 100 : null);
  return { income, needs, wants, savings, needsPct: pct(needs), wantsPct: pct(wants), savingsPct: pct(savings) };
};
